<template>
  <div id="CompetitiveProcessesWrapper" class="flex flex-col gap-4">
    <div class="flex flex-col gap-4">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div ref="tabsContainer"
          class="relative flex items-center gap-1 bg-gray-100 bg-opacity-80 p-1 rounded-xl w-fit">
          <div
            class="absolute top-1 left-0 bg-white rounded-lg shadow-sm transition-all duration-500 ease-in-out pointer-events-none"
            :style="indicatorStyle"></div>

          <button v-for="tab in tabsUI" :key="tab.key" ref="tabs" @click="setStatus(tab.key)" :class="tab.tabClass"
            :disabled="isLoading">
            <span class="relative z-10">{{ tab.label }}</span>
            <span v-if="tab.count !== null" :class="tab.badgeClass">
              {{ tab.count }}
            </span>
          </button>
        </div>

        <label class="relative max-w-md w-full" for="search-pca">
          <svg class="pointer-events-none absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>

          <input v-model="term" @input="onInput" name="search-pca" type="text"
            class="flex w-full border px-3 py-1 shadow-sm md:text-sm pl-10 pr-10 h-11 bg-white border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:ring-opacity-20 focus:border-teal-500 transition-all"
            :placeholder="inputPlaceholder" />
        </label>
      </div>

      <div class="mt-4 flex items-center gap-2" v-if="currentItems.length > 0">
        <span class="text-sm text-gray-400">
          {{ currentCountLabel }}
        </span>
      </div>
    </div>

    <div v-if="isLoading" class="flex flex-col gap-4">
      <CompetitiveProcessesCardSkeleton />
      <CompetitiveProcessesCardSkeleton />
    </div>
    <div v-else-if="error" class="text-red-500 text-sm">
      {{ error }}
    </div>
    <div v-else-if="currentItems.length > 0" class="flex flex-col gap-4">
      <CompetitiveProcessesCard v-for="item in currentItems" :key="item.title" :item="item" :filesTitle="filesTitle"
        :cardStatusOpen="cardStatusOpen" :cardStatusClosed="cardStatusClosed" :buttonMoreLabel="buttonMoreLabel"
        :buttonLessLabel="buttonLessLabel" :tooltipLabel="tooltipLabel"></CompetitiveProcessesCard>
    </div>
    <div v-else class="flex items-center justify-center p-5 my-2">
      <div class="bg-gray-100 rounded-xl p-8 text-center max-w-xl w-full">
        <div class="flex justify-center mb-4">
          <span class="w-10 h-10 text-gray-400 fill-none" v-icon:[emptyIcon]></span>
        </div>

        <h3 class="text-gray-700 text-lg font-semibold mb-2">
          {{ emptyTitle }}
        </h3>

        <p class="text-gray-500 text-sm">
          {{ emptyText }}
        </p>
      </div>
    </div>

    <VPagination v-if="totalPages > 1" :currentPage="currentPage" :totalPages="totalPages" :pageRange="pageRange"
      @page="handlePageChange" blockId="CompetitiveProcessesWrapper" />
  </div>
</template>

<script>
import CompetitiveProcessesCard from "./CompetitiveProcessesCard.vue";
import CompetitiveProcessesCardSkeleton from "./CompetitiveProcessesCardSkeleton.vue";

export default {
  props: {
    apiUrl: {
      type: String,
      required: true
    },
    inputPlaceholder: { default: 'Buscar por título o descripción...' },
    tabs: {
      default: () => ([
        { key: 'open', label: 'Abiertos' },
        { key: 'closed', label: 'Cerrados' },
      ])
    },
    labels: {
      default: () => ({
        open: {
          singular: 'proceso abierto',
          plural: 'procesos abiertos',
        },
        closed: {
          singular: 'proceso cerrado',
          plural: 'procesos cerrados',
        }
      })
    },
    emptyTitle: { default: 'Sin información disponible' },
    emptyText: { default: 'Actualmente no hay datos para mostrar en esta sección. La información aparecerá aquí cuando esté disponible.' },
    emptyIcon: { default: 'article' },
    sortOrder: {
      type: String
    },
    filesTitle: { default: "Documentos adjuntos" },
    cardStatusOpen: { default: "Abierto" },
    cardStatusClosed: { default: "Cerrado" },
    buttonMoreLabel: { default: "Ver más" },
    buttonLessLabel: { default: "Ver menos" },
    tooltipLabel: { default: "Ver resumen IA" },
    tooltipLabel: { default: 'Ver resumen IA' }
  },
  components: {
    CompetitiveProcessesCard,
    CompetitiveProcessesCardSkeleton
  },
  data() {
    return {
      currentItems: [],
      status: "open",
      term: "",
      currentPage: 1,
      totalPages: 1,
      pageRange: 5,
      debounceTimer: null,
      isLoading: false,
      error: null,
      abortController: null,
      counts: {
        open: 0,
        closed: 0
      },
      indicator: {
        width: 0,
        height: 0,
        x: 0,
      },
    };
  },
  computed: {
    tabsUI() {
      return this.tabs.map((tab) => {
        const active = this.status === tab.key;

        return {
          ...tab,
          active,
          count: this.counts[tab.key] ?? null,
          tabClass: [
            "relative flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-500",
            active ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
            this.isLoading ? "cursor-not-allowed" : ""
          ],
          badgeClass: [
            "relative z-10 text-xs font-semibold px-2 py-0.5 rounded-full transition-colors duration-500",
            active
              ? "bg-teal-500 text-white"
              : "bg-gray-200 text-gray-500",
          ],
        };
      });
    },
    currentCountLabel() {
      const count = this.counts[this.status];

      if (count == null) return "";

      const type = this.labels[this.status];
      const text = count === 1 ? type.singular : type.plural;

      return `${count} ${text}`;
    },
    indicatorStyle() {
      return {
        width: `${this.indicator.width}px`,
        height: `${this.indicator.height}px`,
        transform: `translateX(${this.indicator.x}px)`,
      };
    },
  },
  watch: {
    status() {
      this.currentPage = 1;
      this.handleStateChange();
    },
    currentPage() {
      this.handleStateChange();
    }
  },
  created() {
    this.initFromQuery();
    this.handleStateChange();
  },
  beforeDestroy() {
    this.abortController?.abort();
    window.removeEventListener("resize", this.updateIndicator);
  },
  mounted() {
    this.$nextTick(this.updateIndicator);
    window.addEventListener("resize", this.updateIndicator);
  },
  methods: {
    handleStateChange() {
      this.updateQuery();
      this.fetchData();

      this.$nextTick(() => {
        this.updateIndicator();
      });
    },
    updateIndicator() {
      const tabs = this.$refs.tabs;
      const container = this.$refs.tabsContainer;

      if (!tabs?.length || !container) return;

      const index = this.tabs.findIndex(t => t.key === this.status);
      const el = tabs[index];

      if (!el) return;

      const elRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      this.indicator = {
        width: elRect.width,
        height: elRect.height,
        x: elRect.left - containerRect.left,
      };
    },
    onInput() {
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1;
        this.handleStateChange();
      }, 400);
    },
    setStatus(value) {
      if (this.status === value) return;
      this.status = value;
    },
    handlePageChange(page) {
      if (this.currentPage === page) return;
      this.currentPage = page;
    },
    updateQuery() {
      const params = new URLSearchParams(window.location.search);
      params.set("status", this.status);

      if (this.term) params.set("term", this.term);
      else params.delete("term");

      if (this.currentPage > 1) params.set("page", this.currentPage);
      else params.delete("page");

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, "", newUrl);
    },
    initFromQuery() {
      const params = new URLSearchParams(window.location.search);
      const status = params.get("status");
      const term = params.get("term");
      const page = params.get("page");

      if (status) this.status = status;
      if (term) this.term = term;
      if (page) this.currentPage = Number(page);
    },
    async fetchData() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      this.isLoading = true;
      this.error = null;

      try {
        const params = new URLSearchParams();

        params.append('status', this.status);
        params.append('page', this.currentPage);

        if (this.term) params.append("term", this.term);
        if (this.sortOrder) params.append("sort_order", this.sortOrder);

        const res = await fetch(`${this.apiUrl}?${params}`, {
          signal: this.abortController.signal,
        });

        if (!res.ok) {
          throw new Error(`Error ${res.status}`);
        }

        const data = await res.json();

        this.currentItems = data.items ?? [];
        this.totalPages = data.pager.total ?? 1;
        this.counts = {
          open: data.totals.open ?? 0,
          closed: data.totals.closed ?? 0
        };
      } catch (error) {
        if (error.name === "AbortError") return;

        this.error = error.message || "Error inesperado";
        this.currentItems = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
#CompetitiveProcessesWrapper {
  scroll-margin-block-start: 180px;
}
</style>